import { TournamentType } from "../pages/tournament/hook/useEditTournament";
import instance from "../utils/network";
import {
  PaymentPrizeRequest,
  PointDistributionDetail,
  SeedDistributionDetail,
} from "./tournament-prize";

export enum PaymentMethod {
  CASH = "CASH", // 현금
  CARD = "CARD", // 카드
  BANK_TRANSFER = "BANK_TRANSFER", // 계좌이체
  POINTS = "POINTS", // 포인트
  OTHER = "OTHER", // 기타
  DISCOUNT = "DISCOUNT",
  SEED = "SEED", // 크레딧
}

export enum EntryType {
  BUY_IN = "BUY_IN", // 바이인
  RE_BUY = "RE_BUY", // 리엔트리
  ADD_ON = "ADD_ON", // 애드온
}

export enum ExpenseMainType {
  TAX_PUBLIC = "TAX_PUBLIC", // [1] 세금과공과
  RABBIT_PURCHASE = "RABBIT_PURCHASE", // [2] 료열티외
  LABOR = "LABOR", // [3] 인건비
  RENT_MANAGEMENT = "RENT_MANAGEMENT", // [4] 임차료및관리비
  SALARY = "SALARY", // [5] 급여
  DRINK_PURCHASE = "DRINK_PURCHASE", // [6] 주류음료매입
  TAX_LABOR_BOOK = "TAX_LABOR_BOOK", // [7] 세금노무기장
  MARKETING = "MARKETING", // [8] 광고마케팅비
  INSURANCE_COMM = "INSURANCE_COMM", // [9] 보험료및통신비
  SUPPLIES_RENTAL = "SUPPLIES_RENTAL", // [10] 비품및렌탈
  ETC = "ETC", // [11] 기타
  INTERIOR = "INTERIOR", // [12] 인테리어
}

export enum ExpenseSubType {
  // [1] 세금과공과
  TAX = "TAX", // [1-1]
  PUBLIC_CHARGE = "PUBLIC_CHARGE", // [1-2]

  // [3] 인건비
  DEALER = "DEALER", // [3-1]
  COUNTER = "COUNTER", // [3-2]
  FLOOR = "FLOOR", // [3-3]

  // [4] 임차료 및 관리비
  RENT = "RENT", // [4-1]
  MANAGEMENT_FEE = "MANAGEMENT_FEE", // [4-2]

  // [9] 보험료 및 통신비
  INSURANCE = "INSURANCE", // [9-1]
  COMMUNICATION = "COMMUNICATION", // [9-2]
}

export interface SettlementTournament {
  totalMoneyInCount: number;
  tournamentId: number;
  title: string;
  startDate: string;
  entryCount: number;
  userCount: number;
  tournamentType: TournamentType;
  usedPoint: number;
  usedSeed: number;
  distributionDetails: PointDistributionDetail[];
  seedDistributionDetails: SeedDistributionDetail[];
  isEnd: boolean;
  notCountAddon: number;
  additionalSeed: number;
  isRefund: boolean;
}

export interface SettlementInComeDto {
  cash: number;
  card: number;
  bankTransfer: number;
  other: number;
}
export interface SettlementLogResponse {
  date: string;
  pubName: string;
  nickname: string;
  type: string;
  previousAmount: number;
  amount: number;
  finalAmount: number;
  memo: string;
}
export interface SettlementResponse {
  income: SettlementInComeDto;
  tournaments: SettlementTournament[];
  transactionPoint: number;
  weeklyTransactionPoint: number;
  pubPoint: number;
  totalMemberPoint: number;
  totalDiscount: number;
  expense: SettlementExpenseDto[];
  seedOut: number;
  carrotOut?: number; // 매장 → 회원 캐럿(CR) 시상
  seedIn: number;
  pubSeed: number;
  pubCarrot?: number; // 매장 보유 캐럿(CR)
  prizeWait: number;
  pointPrize: number;
  memberMileage: number; // 회원이 보유한 총 마일리지
  mileageOut: number; // 수동지급
  mileageUsed: number; // 회원 마일리지 사용
}
export interface SettlementExpenseDto {
  id?: number;
  description: string;
  amount: number;
  settledAt?: string;
  createdAt?: string;
  updatedAt?: string;
  mainType: ExpenseMainType;
  subType: ExpenseSubType | null;
  paymentMethod: PaymentMethod;
}

export interface SettlementRevenueDto {
  id?: number;
  createdAt?: string;
  updatedAt?: string;
  pubId: number;
  description: string; // 지출 항목
  amount: number;
  settledAt: string;
  paymentMethod: PaymentMethod;
}

export interface SettlementTournamentDto {
  nickname: string;
  memberId: number;
  data: SettlementTournamentMember[];
}

export interface SettlementTournamentResponse {
  list: SettlementTournamentDto[];
  totalPrice: number;
  totalCash: number;
  totalCard: number;
  totalBank: number;
  totalPoint: number;
  totalSeed: number;
  totalDiscount: number;
}

export interface PayMent {
  paymentAmount: number;
  paymentMethod: PaymentMethod;
  memo: string | null;
}

export interface SettlementTournamentMember {
  payment: PayMent[];
  paymentAmount: number;
  entryType: EntryType;
}

export interface SettledDailyReportResponse {
  entryCount: number;
  totalRevenue: number;
  totalExpense: number;
  finalNetProfit: number;
  totalCashRevenue: number;
  totalBankRevenue: number;
  totalCardRevenue: number;
  settledAt: string;
  expense: SettlementExpenseDto[];
}

export interface SettledReportResponse {
  daily: SettledDailyReportResponse[];
  transactionPoint: number;
  getPoint: number;
  tournamentPoint: number;
  entryPoint: number;
  expenseSeed: number;
  incomeSeed: number;
  royaltySeed: number;
  expenseCarrot: number;
  royaltyCarrot: number;
  depositPoint: number;
  promotionPoint: number;
  discount: [SettledReportDiscount[]];
}

export interface SettledReportDiscount {
  amount: number;
  createdAt: string;
  id: number;
  memberId: number;
  memo: string;
  participantId: number;
  paymentMethod: PaymentMethod;
  pubId: number;
  tournamentId: number;
}

export interface SeedPaymentHistory {
  nickname: string;
  amount: number;
  createdAt: Date;
}

export function getExpense(
  id: number,
  date: string,
): Promise<SettlementExpenseDto[]> {
  return instance.get(`/seller/settlement/expense/${id}?date=${date}`);
}
export function getSettlement(
  id: number,
  date: string,
): Promise<SettlementResponse> {
  return instance.get(`/seller/settlement/pub/${id}?date=${date}`);
}

// 구래빗(seed_log) 로그
export function getSettlementLegacySeedLogs(
  id: number,
  date: string,
  dateFilter: string,
): Promise<SettlementLogResponse[]> {
  return instance.get(
    `/seller/settlement/seed-logs/${id}?date=${date}&type=${dateFilter}`,
  );
}

// 신래빗(rabbit_log) 로그
export function getSettlementRabbitLogs(
  id: number,
  date: string,
  dateFilter: string,
): Promise<SettlementLogResponse[]> {
  return instance.get(
    `/seller/settlement/rabbit-logs/${id}?date=${date}&type=${dateFilter}`,
  );
}

// 캐럿(carrot_log) 로그
export function getSettlementCarrotLogs(
  id: number,
  date: string,
  dateFilter: string,
): Promise<SettlementLogResponse[]> {
  return instance.get(
    `/seller/settlement/carrot-logs/${id}?date=${date}&type=${dateFilter}`,
  );
}

// 구래빗 + 신래빗 로그 (최신순)
export async function getSettlementSeedLogs(
  id: number,
  date: string,
  dateFilter: string,
): Promise<SettlementLogResponse[]> {
  const [legacyLogs, rabbitLogs] = await Promise.all([
    getSettlementLegacySeedLogs(id, date, dateFilter),
    getSettlementRabbitLogs(id, date, dateFilter),
  ]);
  return [...(rabbitLogs ?? []), ...(legacyLogs ?? [])].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
}

export function getSettlementPubPointLogs(
  id: number,
  date: string,
  dateFilter: string,
): Promise<SettlementLogResponse[]> {
  return instance.get(
    `/seller/settlement/pub-point-logs/${id}?date=${date}&type=${dateFilter}`,
  );
}

export function getSeedPaymentHistory(
  tournamentId: number,
): Promise<SeedPaymentHistory[]> {
  return instance.get(`/seller/settlement/seed-payment/${tournamentId}`);
}

export function saveRevenue(
  request: SettlementRevenueDto,
  pubId: number,
  date: string,
) {
  return instance.post(
    `/seller/settlement/revenue/${pubId}?date=${date}`,
    request,
  );
}

export function updateRevenue(request: SettlementRevenueDto, id: number) {
  return instance.put(`/seller/settlement/revenue/${id}`, request);
}

export function deleteRevenue(id: number) {
  return instance.delete(`/seller/settlement/revenue/${id}`);
}

export function saveExpense(
  request: SettlementExpenseDto,
  pubId: number,
  date: string,
) {
  return instance.post(
    `/seller/settlement/expense/${pubId}?date=${date}`,
    request,
  );
}

export function updateExpense(request: SettlementExpenseDto, id: number) {
  return instance.put(`/seller/settlement/expense/${id}`, request);
}

export function deleteExpense(id: number) {
  return instance.delete(`/seller/settlement/expense/${id}`);
}

export function getSettlementReport(
  id: number,
  type: string,
): Promise<SettledReportResponse> {
  return instance.get(`/seller/settlement/report/${id}?type=${type}`);
}

export function getSettlementDateReport(
  id: number,
  type: string,
  first: string,
  last: string,
): Promise<SettledReportResponse> {
  return instance.get(
    `/seller/settlement/report/${id}?type=${type}&date=${first}&endDate=${last}`,
  );
}

export function paymentPrize(request: PaymentPrizeRequest[]) {
  return instance.post(`/seller/tournament/point-seed/rank`, request);
}

export function paymentUserPrizePoint(request: PaymentPrizeRequest) {
  return instance.post(`/seller/tournament/pub-point/single-rank`, request);
}

export function resetPrize(tournamentId: number) {
  return instance.delete(`/seller/tournament/point-seed/rank/${tournamentId}`);
}

export function resetUserPrizePoint(request: PaymentPrizeRequest) {
  return instance.delete(`/seller/tournament/pub-point/single-rank`, {
    data: request,
  });
}

export function getSettlementTournament(
  id: number,
): Promise<SettlementTournamentResponse> {
  return instance.get(`/seller/settlement/tournament/${id}`);
}
